// src/components/HeaderTitle/HeaderTitle.jsx

import PropTypes from "prop-types";
import { Fade, Slide, Zoom } from "react-awesome-reveal";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const TONES = {
  blue: {
    shell:
      "border-sky-200/80 bg-gradient-to-br from-white via-sky-50/90 to-blue-50/80 dark:border-sky-500/20 dark:from-slate-900 dark:via-slate-900 dark:to-sky-950/40",
    icon: "bg-sky-600 text-white shadow-sky-500/25 dark:bg-sky-500",
    pill: "bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20",
    glowA: "bg-sky-400/25",
    glowB: "bg-blue-500/20",
    line: "from-sky-500 via-blue-500 to-indigo-500",
  },

  orange: {
    shell:
      "border-orange-200/80 bg-gradient-to-br from-white via-orange-50/90 to-amber-50/80 dark:border-orange-500/20 dark:from-slate-900 dark:via-slate-900 dark:to-orange-950/40",
    icon: "bg-orange-600 text-white shadow-orange-500/25 dark:bg-orange-500",
    pill: "bg-orange-100 text-orange-700 ring-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/20",
    glowA: "bg-orange-400/25",
    glowB: "bg-amber-500/20",
    line: "from-orange-500 via-amber-500 to-yellow-400",
  },

  emerald: {
    shell:
      "border-emerald-200/80 bg-gradient-to-br from-white via-emerald-50/90 to-teal-50/80 dark:border-emerald-500/20 dark:from-slate-900 dark:via-slate-900 dark:to-emerald-950/40",
    icon: "bg-emerald-600 text-white shadow-emerald-500/25 dark:bg-emerald-500",
    pill: "bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
    glowA: "bg-emerald-400/25",
    glowB: "bg-teal-500/20",
    line: "from-emerald-500 via-teal-500 to-cyan-500",
  },

  violet: {
    shell:
      "border-violet-200/80 bg-gradient-to-br from-white via-violet-50/90 to-fuchsia-50/80 dark:border-violet-500/20 dark:from-slate-900 dark:via-slate-900 dark:to-violet-950/40",
    icon: "bg-violet-600 text-white shadow-violet-500/25 dark:bg-violet-500",
    pill: "bg-violet-100 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20",
    glowA: "bg-violet-400/25",
    glowB: "bg-fuchsia-500/20",
    line: "from-violet-500 via-fuchsia-500 to-pink-500",
  },

  rose: {
    shell:
      "border-rose-200/80 bg-gradient-to-br from-white via-rose-50/90 to-pink-50/80 dark:border-rose-500/20 dark:from-slate-900 dark:via-slate-900 dark:to-rose-950/40",
    icon: "bg-rose-600 text-white shadow-rose-500/25 dark:bg-rose-500",
    pill: "bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
    glowA: "bg-rose-400/25",
    glowB: "bg-pink-500/20",
    line: "from-rose-500 via-pink-500 to-orange-400",
  },

  slate: {
    shell:
      "border-slate-200/80 bg-gradient-to-br from-white via-slate-50/90 to-slate-100/80 dark:border-slate-800 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950",
    icon: "bg-slate-950 text-white shadow-slate-950/20 dark:bg-white dark:text-slate-950",
    pill: "bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
    glowA: "bg-slate-400/20",
    glowB: "bg-slate-500/15",
    line: "from-slate-700 via-slate-500 to-slate-400",
  },

  red: {
    shell:
      "border-red-200/80 bg-gradient-to-br from-white via-red-50/90 to-rose-50/80 dark:border-red-500/20 dark:from-slate-900 dark:via-slate-900 dark:to-red-950/40",
    icon: "bg-red-600 text-white shadow-red-500/25 dark:bg-red-500",
    pill: "bg-red-100 text-red-700 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20",
    glowA: "bg-red-400/25",
    glowB: "bg-rose-500/20",
    line: "from-red-600 via-rose-500 to-orange-500",
  },
};

function getToneFromRole({ roleId, role }) {
  const numericRoleId = Number(roleId);

  if (numericRoleId === 1) return "red";
  if (numericRoleId === 2) return "blue";
  if (numericRoleId === 3) return "emerald";

  const roleText = String(role || "").toLowerCase();

  if (roleText.includes("admin")) return "red";
  if (roleText.includes("enseignant") || roleText.includes("coach")) {
    return "emerald";
  }
  if (roleText.includes("utilisateur") || roleText.includes("user")) {
    return "blue";
  }

  return "blue";
}

export default function HeaderTitle({
  role = "Administrateur",
  roleId = null,
  title = "",
  subtitle = "",
  icon: Icon = null,
  align = "left",
  tone = "auto",
  compact = false,
  className = "",
}) {
  const resolvedTone =
  tone === "auto" ? getToneFromRole({ roleId, role }) : tone;

const theme = TONES[resolvedTone] || TONES.blue;

  const alignment =
    align === "center"
      ? "items-center text-center"
      : align === "right"
        ? "items-end text-right"
        : "items-start text-left";

  const contentAlignment =
    align === "center"
      ? "justify-center"
      : align === "right"
        ? "justify-end"
        : "justify-start";

  return (
    <header
      className={cn(
        "relative overflow-hidden rounded-[1.75rem] border shadow-sm backdrop-blur",
        theme.shell,
        compact ? "p-3 sm:p-4" : "p-4 sm:p-5",
        className,
      )}
    >
      <div
        className={cn(
          "pointer-events-none absolute -right-12 -top-14 h-32 w-32 rounded-full blur-3xl",
          theme.glowA,
        )}
      />
      <div
        className={cn(
          "pointer-events-none absolute -bottom-16 -left-14 h-36 w-36 rounded-full blur-3xl",
          theme.glowB,
        )}
      />

      <div className="pointer-events-none absolute inset-x-0 top-0 h-1 overflow-hidden">
        <div className={cn("h-full bg-gradient-to-r", theme.line)} />
      </div>

      <div className={cn("relative flex flex-col gap-3", alignment)}>
        <div
          className={cn(
            "flex w-full flex-col gap-3 sm:flex-row sm:items-center",
            contentAlignment,
          )}
        >
          {Icon && (
            <Zoom triggerOnce duration={450}>
              <div
                className={cn(
                  "flex shrink-0 items-center justify-center rounded-2xl shadow-lg",
                  compact ? "h-10 w-10" : "h-12 w-12",
                  theme.icon,
                )}
              >
                <Icon className={compact ? "text-base" : "text-lg"} />
              </div>
            </Zoom>
          )}

          <div className="min-w-0">
            <Fade triggerOnce cascade damping={0.08} duration={420}>
              <p
                className={cn(
                  "inline-flex items-center rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] ring-1",
                  theme.pill,
                )}
              >
                {String(role).toUpperCase()}
              </p>

              <h1
                className={cn(
                  "mt-2 font-montserrat font-bold tracking-tight text-slate-950 dark:text-white",
                  compact
                    ? "text-xl sm:text-2xl"
                    : "text-2xl sm:text-3xl lg:text-4xl",
                )}
              >
                {title}
              </h1>
            </Fade>
          </div>
        </div>

        {subtitle && (
          <Slide triggerOnce direction="up" duration={420}>
            <p
              className={cn(
                "max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300",
                align === "center" && "mx-auto",
                compact ? "mt-0" : "mt-1",
              )}
            >
              {subtitle}
            </p>
          </Slide>
        )}
      </div>
    </header>
  );
}

HeaderTitle.propTypes = {
  role: PropTypes.string,
  roleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.elementType,
  align: PropTypes.oneOf(["left", "center", "right"]),
  tone: PropTypes.oneOf([
    "auto",
    "blue",
    "orange",
    "emerald",
    "violet",
    "rose",
    "red",
    "slate",
  ]),
  compact: PropTypes.bool,
  className: PropTypes.string,
};
